<template>
    <div class="modal-container modal-container--wizard" @keydown.enter="handleEnter">
        <div class="modal-header modal-header--title">
            <h2 class="modal-title">Add Employee</h2>
            <button type="button" class="close-btn" :disabled="isLoading" @click="closeDialog">
                <PhX :size="20" />
            </button>
        </div>

        <!-- Choice Step -->
        <div v-if="flowMode === 'choice'" class="modal-body modal-body--wizard">
            <p class="choice-prompt">How would you like to add this employee?</p>
            <p class="choice-subtitle">Choose the method that best fits your workflow.</p>
            <div class="choice-options">
                <div
                    class="choice-card"
                    @click="selectMethod('invite')"
                >
                    <PhEnvelope :size="24" class="choice-icon" />
                    <div class="choice-content">
                        <span class="choice-title">Send Invite</span>
                        <span class="choice-desc">Employee receives an email to set up their own account. Best for HR onboarding.</span>
                    </div>
                </div>
                <div
                    class="choice-card"
                    @click="selectMethod('manual')"
                >
                    <PhPencilSimple :size="24" class="choice-icon" />
                    <div class="choice-content">
                        <span class="choice-title">Add Manually</span>
                        <span class="choice-desc">You fill out the full profile. Best for EA managing C-Suite or non-self-service users.</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Send Invite Flow -->
        <template v-else-if="flowMode === 'invite'">
            <div v-if="!inviteSuccess" class="modal-body modal-body--wizard">
                <div class="info-banner">
                    The employee receives a link to set up their own {{ companyName }} account.
                </div>
                <div class="form-row">
                    <label :for="fieldId('first_name')" class="label-with-tag"><span>First Name</span><span class="optional-tag">Optional</span></label>
                    <PrimeInputText :id="fieldId('first_name')" v-model="formData.first_name" placeholder="First name" class="w-full" v-bind="fieldA11y('first_name')" />
                    <p v-if="fieldErrors.first_name" :id="errorId('first_name')" class="field-error">{{ fieldErrors.first_name }}</p>
                </div>
                <div class="form-row">
                    <label :for="fieldId('last_name')" class="label-with-tag"><span>Last Name</span><span class="optional-tag">Optional</span></label>
                    <PrimeInputText :id="fieldId('last_name')" v-model="formData.last_name" placeholder="Last name" class="w-full" v-bind="fieldA11y('last_name')" />
                    <p v-if="fieldErrors.last_name" :id="errorId('last_name')" class="field-error">{{ fieldErrors.last_name }}</p>
                </div>
                <div class="form-row">
                    <label :for="fieldId('email')">Work Email <span aria-hidden="true">*</span></label>
                    <PrimeInputText :id="fieldId('email')" v-model="formData.email" placeholder="email@company.com" class="w-full" type="email" v-bind="fieldA11y('email', 'invite-license-sentence', true)" />
                    <p v-if="fieldErrors.email" :id="errorId('email')" class="field-error">{{ fieldErrors.email }}</p>
                </div>
                <!-- What happens to the license, keyed on Auto-Provision AND free seats (D6):
                     the toggle alone lies when the pool is empty. Part of the email field's
                     description so a screen reader hears it with the field. -->
                <p id="invite-license-sentence" class="license-hint">
                    {{ licenseSentence }}
                    <button v-if="licenseOutlook === 'none_free' && canBuySeats" type="button" class="inline-link" @click="openPurchaseLicenses(autoProvisionSlug)">Buy licenses</button>
                    <NuxtLink v-if="canChangeSettings" :to="`/business/${companySlug ?? ''}/settings`" class="inline-link" @click="closeDialog">Change in Settings</NuxtLink>
                </p>
                <button v-if="awaitingSeats" type="button" class="skip-link" @click="refreshSeats">
                    Bought licenses in the other tab? Refresh licenses
                </button>
            </div>
            <div v-else class="modal-body modal-body--wizard invite-success">
                <PhCheckCircle v-if="creation.invite_sent !== false" :size="48" weight="fill" class="success-icon text-green-500" />
                <PhWarningCircle v-else :size="48" weight="fill" class="success-icon text-amber-500" />
                <h3 class="success-title">{{ creation.invite_sent === false ? 'Employee added' : 'Invite Sent!' }}</h3>
                <p class="success-message">
                    <template v-if="creation.invite_sent === false">Employee added, but the welcome email couldn't be sent. Resend from Pending invitations.</template>
                    <template v-else>{{ formData.email }} will receive an email to set up their account.</template>
                </p>
                <div class="success-summary">
                    <div class="summary-row">
                        <span>Name</span>
                        <span>{{ inviteDisplayName }}</span>
                    </div>
                    <div class="summary-row">
                        <span>Email</span>
                        <span>{{ formData.email }}</span>
                    </div>
                    <div v-if="licenseOutcomeText" class="summary-row">
                        <span>License</span>
                        <span>{{ licenseOutcomeText }}</span>
                    </div>
                </div>
                <div class="info-alert">
                    <PhInfo :size="20" />
                    <span>
                        <template v-if="creation.license_assigned">You can change or remove the license from their profile at any time.</template>
                        <template v-else>Use Assign License to give them one now. They don't need to sign in first.</template>
                        <button v-if="creation.license_reason === 'no_seats' && canBuySeats" type="button" class="inline-link" @click="openPurchaseLicenses(autoProvisionSlug)">Buy licenses</button>
                    </span>
                </div>
            </div>
        </template>

        <!-- Add Manually Flow (4 steps) -->
        <template v-else-if="flowMode === 'manual'">
            <div class="stepper">
                <div
                    v-for="(step, i) in manualSteps"
                    :key="step.id"
                    class="stepper-item"
                    :class="{ active: manualStep === i + 1, completed: manualStep > i + 1 }"
                >
                    <div class="stepper-circle">
                        <PhCheck v-if="manualStep > i + 1" :size="16" weight="bold" />
                        <span v-else>{{ i + 1 }}</span>
                    </div>
                    <span class="stepper-label">{{ step.label }}</span>
                    <div v-if="i < manualSteps.length - 1" class="stepper-line" :class="{ completed: manualStep > i + 1 }" />
                </div>
            </div>

            <div class="modal-body modal-body--wizard">
                <!-- Step 1: Employee Details -->
                <div v-show="manualStep === 1" class="step-content">
                    <div class="form-row">
                        <label :for="fieldId('first_name')">First Name <span aria-hidden="true">*</span></label>
                        <PrimeInputText :id="fieldId('first_name')" v-model="formData.first_name" placeholder="First name" class="w-full" v-bind="fieldA11y('first_name', undefined, true)" />
                        <p v-if="fieldErrors.first_name" :id="errorId('first_name')" class="field-error">{{ fieldErrors.first_name }}</p>
                    </div>
                    <div class="form-row">
                        <label :for="fieldId('last_name')">Last Name <span aria-hidden="true">*</span></label>
                        <PrimeInputText :id="fieldId('last_name')" v-model="formData.last_name" placeholder="Last name" class="w-full" v-bind="fieldA11y('last_name', undefined, true)" />
                        <p v-if="fieldErrors.last_name" :id="errorId('last_name')" class="field-error">{{ fieldErrors.last_name }}</p>
                    </div>
                    <div class="form-row">
                        <label :for="fieldId('email')">Work Email <span aria-hidden="true">*</span></label>
                        <PrimeInputText :id="fieldId('email')" v-model="formData.email" placeholder="email@company.com" class="w-full" type="email" v-bind="fieldA11y('email', undefined, true)" />
                        <p v-if="fieldErrors.email" :id="errorId('email')" class="field-error">{{ fieldErrors.email }}</p>
                    </div>

                    <div class="dob-phone-row">
                        <div class="form-row form-row--dob" ref="dobAnchorRef">
                            <label :for="fieldId('dob')">Date of Birth <span aria-hidden="true">*</span></label>
                            <button
                                :id="fieldId('dob')"
                                type="button"
                                class="dob-trigger"
                                v-bind="fieldA11y('dob', 'manual-pii-reason', true)"
                                @click="showDobPicker = !showDobPicker"
                            >
                                <PhCalendarBlank :size="18" class="text-zinc-500" />
                                <span :class="{ 'text-zinc-400': !formData.dob }">
                                    {{ formatDate(formData.dob) || 'Pick a date' }}
                                </span>
                                <button
                                    v-if="formData.dob"
                                    type="button"
                                    class="dob-clear"
                                    @click.stop="formData.dob = ''"
                                    aria-label="Clear date"
                                >
                                    <PhX :size="14" />
                                </button>
                            </button>
                            <p v-if="fieldErrors.dob" :id="errorId('dob')" class="field-error">{{ fieldErrors.dob }}</p>
                            <!-- Float the picker over the surrounding form rows so opening
                                 it doesn't push the Phone/Address sections down. The
                                 outer .form-row anchors via position:relative; the
                                 picker itself is absolutely positioned. -->
                            <div v-if="showDobPicker" ref="dobPopoverRef" class="dob-popover">
                                <CustomDatePicker
                                    :initial-date="dobInitialDate"
                                    :min-age="18"
                                    @cancel="showDobPicker = false"
                                    @apply="onDobApplied"
                                />
                            </div>
                        </div>

                        <div class="form-row">
                            <label :for="fieldId('phone')" class="label-with-tag">
                                <span>Phone</span>
                                <span class="optional-tag">Optional</span>
                            </label>
                            <PhoneFields
                                v-model="formData.phone"
                                :input-id="fieldId('phone')"
                                :error="fieldErrors.phone"
                            />
                        </div>
                    </div>
                    <!-- Why the manual path asks for what the invite path does not (D29). -->
                    <p id="manual-pii-reason" class="field-reason">Needed to find and remove this person's exposures.</p>

                    <div class="form-row" role="group" :aria-labelledby="`${fieldId('address')}-label`" aria-describedby="manual-pii-reason">
                        <span :id="`${fieldId('address')}-label`" class="group-label">Address <span aria-hidden="true">*</span></span>
                        <AddressFields v-model="formData.address" :required="true" :id-prefix="fieldId('address')" :errors="addressErrors" />
                    </div>
                </div>

                <!-- Step 2: Role & Department -->
                <div v-show="manualStep === 2" class="step-content">
                    <!-- Roles sit on the stricter gate (D22), as in the drawer: without
                         role-management write the API refuses the whole create, so the
                         picker is not offered and the person is added as Employee. -->
                    <div v-if="canAssignRoles" class="form-row">
                        <label>Role</label>
                        <PrimeSelect
                            v-model="formData.role"
                            :options="roles"
                            optionLabel="name"
                            optionValue="name"
                            placeholder="Select role"
                            class="role-select w-full"
                        />
                    </div>
                    <div class="form-row">
                        <label>Department</label>
                        <PrimeSelect
                            v-model="formData.department_id"
                            :options="departments"
                            optionLabel="name"
                            optionValue="id"
                            placeholder="Select department"
                            class="w-full"
                        />
                    </div>
                </div>

                <!-- Step 3: License -->
                <div v-show="manualStep === 3" class="step-content">
                    <p class="license-instruction">Assign a license to this employee, or skip for now.</p>
                    <div class="license-cards">
                        <div
                            v-for="lic in licenseOptions"
                            :key="lic.slug"
                            class="license-card"
                            :class="{ selected: selectedLicense === lic.slug, disabled: lic.outOfStock }"
                            @click="!lic.outOfStock ? selectedLicense = lic.slug : null"
                        >
                            <div class="license-card-header">
                                <span class="license-name">{{ lic.name }}</span>
                                <!-- A pooled seat costs nothing to hand out, so no price here; it
                                     appears only where money changes hands (D29). -->
                                <span class="license-meta">{{ lic.available ?? 0 }} available</span>
                            </div>
                            <button
                                v-if="lic.outOfStock && canBuySeats"
                                type="button"
                                class="purchase-more-link"
                                @click.stop="openPurchaseLicenses(lic.slug)"
                            >Purchase More · {{ formatCurrency(lic.price) }}/yr</button>
                        </div>
                    </div>
                    <button
                        v-if="awaitingSeats"
                        type="button"
                        class="skip-link"
                        @click="refreshSeats"
                    >
                        Bought licenses in the other tab? Refresh licenses
                    </button>
                    <button type="button" class="skip-link" @click="selectedLicense = ''">
                        Skip for now — assign license later from the Employees profile
                    </button>
                </div>

                <!-- Step 4: Complete -->
                <div v-show="manualStep === 4" class="step-content complete-step">
                    <PhCheckCircle :size="48" weight="fill" class="success-icon text-green-500" />
                    <h3 class="success-title">Employee Added Successfully</h3>
                    <p class="success-message">Profile created by admin. No invitation email was sent.</p>
                    <div class="complete-summary">
                        <div class="summary-row">
                            <span>Name</span>
                            <span>{{ formData.first_name }} {{ formData.last_name }}</span>
                        </div>
                        <div class="summary-row">
                            <span>Email</span>
                            <span>{{ formData.email }}</span>
                        </div>
                        <div class="summary-row">
                            <span>Role</span>
                            <span>{{ formData.role || '—' }}</span>
                        </div>
                        <div class="summary-row">
                            <span>Department</span>
                            <span>{{ getDepartmentName(formData.department_id) || '—' }}</span>
                        </div>
                        <div class="summary-row">
                            <span>License</span>
                            <span>{{ manualLicenseText }}</span>
                        </div>
                    </div>
                    <div class="info-alert">
                        <PhInfo :size="20" />
                        <span>Send an invite now, or share the Privacy Bee app URL when they're ready to log in.</span>
                    </div>
                    <div class="send-invite-row">
                        <PrimeButton
                            v-if="inviteSend !== 'sent'"
                            label="Send invite"
                            severity="secondary"
                            outlined
                            :loading="inviteSend === 'sending'"
                            :disabled="inviteSend === 'sending'"
                            @click="sendInvite"
                        />
                        <p v-if="inviteSend === 'sent'" class="send-invite-status" role="status">Invite sent to {{ formData.email }}.</p>
                        <p v-else-if="inviteSend === 'failed'" class="send-invite-status send-invite-status--failed" role="status">Couldn't send the invite. Try again, or send it from their profile.</p>
                    </div>
                </div>
            </div>
        </template>

        <!-- The choice step has no bottom action bar: selecting a card navigates
             straight into that flow (the header X cancels). -->
        <div v-if="flowMode !== 'choice'" class="modal-footer modal-footer--wizard">
            <!-- Invite flow - form -->
            <template v-if="flowMode === 'invite' && !inviteSuccess">
                <PrimeButton label="Back" severity="secondary" text @click="backToChoice" :disabled="isLoading">
                    <template #icon>
                        <PhCaretLeft :size="16" />
                    </template>
                </PrimeButton>
                <p v-if="inviteSubmitReason" id="invite-submit-reason" class="submit-reason">{{ inviteSubmitReason }}</p>
                <div v-else class="flex-1" />
                <PrimeButton label="Cancel" severity="secondary" @click="closeDialog" :disabled="isLoading" />
                <PrimeButton
                    label="Send Invite"
                    :loading="isLoading"
                    :disabled="!canSubmitInvite"
                    :aria-describedby="inviteSubmitReason ? 'invite-submit-reason' : undefined"
                    @click="submitInvite"
                />
            </template>

            <!-- Invite flow - success -->
            <template v-else-if="flowMode === 'invite' && inviteSuccess">
                <!-- Offer it only when they don't already have one: a license the API assigned
                     in the same request is reported here, and the copy above already says to
                     change it from the profile. `null` means the API said nothing, so still offer. -->
                <PrimeButton v-if="!creation.license_assigned" label="Assign License" severity="secondary" @click="goToAssignLicense" />
                <PrimeButton label="Done" @click="finishAndClose">
                    <template #icon>
                        <PhCheck :size="16" weight="bold" />
                    </template>
                </PrimeButton>
            </template>

            <!-- Manual flow - steps 1-3 -->
            <template v-else-if="flowMode === 'manual' && manualStep < 4">
                <PrimeButton v-if="manualStep > 1" label="Back" severity="secondary" text @click="manualStep--">
                    <template #icon>
                        <PhCaretLeft :size="16" />
                    </template>
                </PrimeButton>
                <p v-if="manualStep === 1 && detailsSubmitReason" id="manual-submit-reason" class="submit-reason">{{ detailsSubmitReason }}</p>
                <div v-else class="flex-1" />
                <PrimeButton label="Cancel" severity="secondary" @click="closeDialog" :disabled="isLoading" />
                <PrimeButton
                    :label="manualStep === 3 ? 'Add Employee' : 'Next'"
                    :loading="isLoading"
                    :disabled="manualStep === 1 && !canSubmitDetails"
                    :aria-describedby="manualStep === 1 && detailsSubmitReason ? 'manual-submit-reason' : undefined"
                    @click="manualStepNext"
                />
            </template>

            <!-- Manual flow - complete -->
            <template v-else-if="flowMode === 'manual' && manualStep === 4">
                <PrimeButton label="Add Another" severity="secondary" outlined @click="addAnother">
                    <template #icon>
                        <PhPlus :size="16" weight="bold" />
                    </template>
                </PrimeButton>
                <PrimeButton label="Done" @click="finishAndClose">
                    <template #icon>
                        <PhCheck :size="16" weight="bold" />
                    </template>
                </PrimeButton>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    PhCalendarBlank,
    PhCaretLeft,
    PhCheck,
    PhCheckCircle,
    PhEnvelope,
    PhInfo,
    PhPencilSimple,
    PhPlus,
    PhWarningCircle,
    PhX,
} from '@phosphor-icons/vue';
import AddressFields from '~/components/shared/forms/AddressFields.vue';
import PhoneFields from '~/components/shared/forms/PhoneFields.vue';
import CustomDatePicker from '~/components/main/shared/CustomDatePicker.vue';
import { formatCurrency } from '@privacy-bee/format/currency';
import { formatDate } from '@privacy-bee/format/date';
import { isValidEmail, normalizeEmail } from '@privacy-bee/validate/email';
import { optionalAddressPayload, optionalPhonePayload } from '~/utils/employee-contact-payload';
import { enterFromField } from '~/utils/dialog-enter';
import { autoProvisionTier } from '~/utils/auto-provision';
import { businessService } from '~/services/business-service';
import { useBusinessStore } from '~/stores/business';
import type { CreatedEmployee, EmployeeCreateInput, EmployeeCreateOutcome, License } from '~/types/business';
import { ApiError, memberFacingMessage } from '~/utils/errors';
import { NO_LICENSE_SLUG, licenseTierName } from '~/types/licenses/tiers';
import { checkoutBusinessUrl } from '~/utils/cross-app-urls';
import { isDemoMode } from '~/services/demo/mode';
import { ModalNames } from '~/types/modals';
import { getDialogRef } from '~/composables/dynamic-dialog';
import type { DynamicDialogInstance } from 'primevue/dynamicdialogoptions';

const dialogRef = getDialogRef(ModalNames.CREATE_EMPLOYEE);
const businessStore = useBusinessStore();
const { showSuccess, showError } = useGlobalToast();

const companyId = computed(() => dialogRef.value?.data?.companyId as number | undefined);
const companySlug = computed(() => dialogRef.value?.data?.companySlug as string | undefined);
const onRefresh = computed(() => dialogRef.value?.data?.onRefresh as (() => Promise<void>) | undefined);
const companyName = computed(() => businessStore.business?.name || 'company');

type FlowMode = 'choice' | 'invite' | 'manual';
type AddMethod = 'invite' | 'manual';

const flowMode = ref<FlowMode>('choice');
const addMethod = ref<AddMethod | null>(null);
const inviteSuccess = ref(false);
const manualStep = ref(1);
const isLoading = ref(false);
const createdEmployee = ref<CreatedEmployee | null>(null);

/** What the API said it did after the row committed; all null until it answers (D8). */
const creation = computed<EmployeeCreateOutcome>(
    () => createdEmployee.value?.creation ?? { license_assigned: null, license_reason: null, invite_sent: null },
);

const manualSteps = [
    { id: 'details', label: 'Employee Details' },
    { id: 'role', label: 'Role & Department' },
    { id: 'license', label: 'License' },
    { id: 'complete', label: 'Complete' },
];

const formData = reactive({
    email: '',
    first_name: '',
    last_name: '',
    role: null as string | null,
    department_id: null as number | null,
    // ISO date string (YYYY-MM-DD); empty = no DOB provided.
    dob: '' as string,
    phone: { country_code: '1', phone_number: '' },
    address: { line_1: '', line_2: '', city: '', region: '', postal_code: '', country: 'US' },
});
const showDobPicker = ref(false);
const dobAnchorRef = ref<HTMLElement | null>(null);
const dobPopoverRef = ref<HTMLElement | null>(null);

// Default the calendar to ~30 years ago when the user hasn't picked a date
// yet — most employees are 25–45, so jumping straight to today's month and
// forcing them to scroll back ~30 years is poor UX.
const dobInitialDate = computed<Date>(() => {
    if (formData.dob) return new Date(formData.dob + 'T12:00:00');
    const now = new Date();
    return new Date(now.getFullYear() - 30, now.getMonth(), now.getDate(), 12, 0, 0);
});

// Close the popover on any pointerdown that lands outside the trigger or
// the popover itself. The trigger is *inside* dobAnchorRef so clicks on it
// won't fire this branch.
function onDocumentPointerDown(e: PointerEvent) {
    if (!showDobPicker.value) return;
    const target = e.target as Node;
    if (dobAnchorRef.value?.contains(target)) return;
    if (dobPopoverRef.value?.contains(target)) return;
    showDobPicker.value = false;
}

watch(showDobPicker, (open) => {
    if (open) {
        document.addEventListener('pointerdown', onDocumentPointerDown);
    } else {
        document.removeEventListener('pointerdown', onDocumentPointerDown);
    }
});

onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', onDocumentPointerDown);
});
const selectedLicense = ref('');

// Only a financial-write admin is offered the purchase; for everyone else the
// out-of-stock tier is unavailable.
const canBuySeats = computed(() => businessStore.hasPermission('financial', 'write'));

// Roles are on the stricter gate (D22): employee-write alone must not hand out Admin.
const canAssignRoles = computed(() => businessStore.hasPermission('role-management', 'write'));

// True once this modal has sent the admin to checkout in another tab.
const awaitingSeats = ref(false);

const roles = ref<Array<{ name: string }>>([]);
// Only trust the store's departments once settings have loaded for THIS company;
// otherwise a modal opened after a settings denial would show the previous
// business's stale departments.
const settingsAvailable = ref(false);
const departments = computed(() => (settingsAvailable.value ? businessStore.settings?.departments || [] : []));
// True when the post-create license assignment failed — the completion step must
// not present the license as assigned in that case.
const licenseAssignmentFailed = ref(false);
const licenses = ref<License[]>([]);

// Regular companies (is_partner=false) buy seats up front, so running out
// of stock should block selection. Channel-partner-owned companies
// (is_partner=true) provision on demand — see EmployeeDetailsDrawer.vue for
// the same gate.
const isPartnerCompany = computed(() => !!businessStore.business?.is_partner);

/** The picked plan's name, read off its catalogue row the way its card in the picker reads it. */
const selectedTierName = computed(() =>
    licenseTierName(licenses.value.find((l) => l.slug === selectedLicense.value) ?? selectedLicense.value),
);

const licenseOptions = computed(() => {
    return licenses.value
        .filter((l) => l.slug)
        .map((l) => ({
            slug: l.slug,
            // The tier-map name, or the catalogue's own for a license outside the
            // ladder (a partner SKU), as the done screen names the pick, so one
            // wizard cannot call the same plan two things.
            name: licenseTierName(l),
            available: l.available ?? 0,
            price: l.price ?? 0,
            outOfStock: !isPartnerCompany.value && (l.available ?? 0) === 0,
        }));
});

// ── Auto-Provision and the license sentence (D6) ─────────────────────────────
// Settings are trusted only once loaded for THIS company (see `departments`); a
// user who may not read them — HR holds employee-write alone — gets the setting
// from the business payload instead. Null until either names it.
const autoProvision = computed(() => autoProvisionTier(settingsAvailable.value ? businessStore.settings : businessStore.business));
const autoProvisionSlug = computed(() => autoProvision.value ?? '');
const autoProvisionTierName = computed(() => {
    const slug = autoProvisionSlug.value;
    if (!slug) return '';
    return licenseTierName({ slug, name: businessStore.settings?.licenses?.find((l) => l.slug === slug)?.name });
});

// The seat counts the page's availability cards are drawn from — the same
// endpoint, so the sentence and the cards can't disagree (D10). Null until read.
const seatCounts = ref<License[] | null>(null);
const seatsFree = computed(() => seatCounts.value?.find((l) => l.slug === autoProvisionSlug.value)?.available ?? 0);

type LicenseOutlook = 'unknown' | 'off' | 'partner' | 'counting' | 'free' | 'none_free';
const licenseOutlook = computed<LicenseOutlook>(() => {
    if (autoProvision.value === null) return 'unknown';
    if (!autoProvisionSlug.value) return 'off';
    if (isPartnerCompany.value) return 'partner';
    if (seatCounts.value === null) return 'counting';
    return seatsFree.value > 0 ? 'free' : 'none_free';
});
const licenseSentence = computed(() => {
    const tier = autoProvisionTierName.value;
    switch (licenseOutlook.value) {
        case 'free':
            return `Auto-Provision is on: a ${tier} license will be assigned right away (${seatsFree.value} left).`;
        case 'none_free':
            return `Auto-Provision is on, but no ${tier} licenses are free. They'll be added without a license.`;
        case 'off':
            return 'Auto-Provision is off: no license will be assigned. You can assign one from their profile at any time.';
        case 'partner':
            return `Auto-Provision is on: a ${tier} license will be provisioned.`;
        case 'counting':
            return `Auto-Provision is on: a ${tier} license will be assigned if one is free.`;
        default:
            return 'You can assign a license from their profile at any time.';
    }
});
const canChangeSettings = computed(() => businessStore.hasPermission('settings', 'write'));

/** The License row on a done screen, from what the API reported (D6, D8); empty when it reported nothing. */
const licenseOutcomeText = computed(() => {
    const { license_assigned, license_reason } = creation.value;
    const tier = autoProvisionTierName.value;
    if (license_assigned === null) return '';
    if (license_assigned) return `${tier || 'License'} (from Auto-Provision)`;
    switch (license_reason) {
        case 'no_seats':
            return `No license assigned: no ${tier ? `${tier} ` : ''}licenses free`;
        case 'unknown_license':
            return "No license assigned: the Auto-Provision license isn't available";
        case 'error':
            return 'No license assigned: the license step failed';
        default:
            return 'No license assigned';
    }
});

async function fetchSeatCounts(): Promise<boolean> {
    if (!companyId.value) return false;
    try {
        seatCounts.value = await businessService.getLicenseInformation(companyId.value);
        return true;
    } catch (error) {
        console.error('Failed to load license counts:', error);
        return false;
    }
}

// ── Field-level errors (D12) ─────────────────────────────────────────────────
// Keyed by this form's own field names; the API's keys are translated once, in
// `fieldFor`, so no internal name or list index reaches the screen.
const fieldErrors = reactive<Record<string, string>>({});
const ADDRESS_KEYS = ['country', 'line_1', 'line_2', 'city', 'region', 'postal_code'] as const;
const FIELD_ORDER = ['first_name', 'last_name', 'email', 'dob', 'phone', ...ADDRESS_KEYS.map((k) => `address.${k}`)];

function fieldFor(apiKey: string): string | null {
    const key = apiKey.replace(/\.\d+(?=\.|$)/, '');
    if (key.startsWith('emails')) return 'email';
    if (key.startsWith('phone_numbers')) return 'phone';
    if (key.startsWith('addresses.')) {
        const part = key.slice('addresses.'.length);
        return (ADDRESS_KEYS as readonly string[]).includes(part) ? `address.${part}` : null;
    }
    return ['first_name', 'last_name', 'dob'].includes(key) ? key : null;
}

/** Ids are per flow so the invite form and the manual wizard never share one. */
const fieldId = (field: string) => `${flowMode.value}-${field.replace('.', '-')}`;
const errorId = (field: string) => `${fieldId(field)}-error`;

/**
 * The invalid/describedby pair for a field, plus any always-on description such as the
 * license sentence, plus whether the field is mandatory.
 *
 * `aria-required` and not the HTML `required` attribute: these are PrimeVue inputs inside a
 * form this component submits itself, and a native `required` would hand validation to the
 * browser's own bubble — which is not where this form reports errors. The asterisk beside
 * each label is `aria-hidden`, so without this line the mandatory fields were announced as
 * though they were optional.
 */
function fieldA11y(field: string, also?: string, required = false) {
    const invalid = !!fieldErrors[field];
    const describedBy = [also, invalid ? errorId(field) : ''].filter(Boolean).join(' ');
    return {
        'aria-invalid': invalid || undefined,
        'aria-describedby': describedBy || undefined,
        'aria-required': required || undefined,
    };
}

const addressErrors = computed(() =>
    Object.fromEntries(ADDRESS_KEYS.filter((k) => fieldErrors[`address.${k}`]).map((k) => [k, fieldErrors[`address.${k}`]])),
);

function clearFieldErrors() {
    for (const key of Object.keys(fieldErrors)) delete fieldErrors[key];
}

/**
 * Places a 422's messages under their fields and moves focus to the first;
 * returns false when none of them belonged to a field, so the caller toasts.
 */
function applyFieldErrors(errors: Record<string, string[]>): boolean {
    for (const [apiKey, messages] of Object.entries(errors)) {
        const field = fieldFor(apiKey);
        if (field && messages?.[0] && !fieldErrors[field]) fieldErrors[field] = messages[0];
    }
    const first = FIELD_ORDER.find((f) => fieldErrors[f]);
    if (!first) return false;
    // The manual wizard submits from step 3 but the fields live on step 1.
    if (flowMode.value === 'manual') manualStep.value = 1;
    nextTick(() => document.getElementById(fieldId(first))?.focus());
    return true;
}

function handleCreateError(error: unknown, fallback: string) {
    if (error instanceof ApiError && applyFieldErrors(error.errors)) return;
    showError('Error', memberFacingMessage(error, fallback));
}

// A message stays until its field changes (D12).
for (const key of ['first_name', 'last_name', 'email', 'dob'] as const) {
    watch(() => formData[key], () => { delete fieldErrors[key]; });
}
watch(() => formData.phone, () => { delete fieldErrors.phone; });
watch(() => formData.address, (next, prev) => {
    for (const k of ADDRESS_KEYS) if (next[k] !== prev?.[k]) delete fieldErrors[`address.${k}`];
});

// Invite requires only a valid work email — name is optional (the employee
// completes their own profile after accepting). A disabled button says why (D12).
// Judged and sent trimmed: a pasted "  harry@privacybee.com  " is that address (D11).
const inviteSubmitReason = computed(() => {
    if (!formData.email.trim()) return 'Enter a work email to continue';
    if (!isValidEmail(normalizeEmail(formData.email))) return 'Enter a valid work email to continue';
    return '';
});
const canSubmitInvite = computed(() => !inviteSubmitReason.value);

const inviteDisplayName = computed(() =>
    [formData.first_name, formData.last_name].map((s) => s.trim()).filter(Boolean).join(' ') || '—'
);

const detailsSubmitReason = computed(() => {
    if (!formData.first_name.trim()) return 'Enter a first name to continue';
    if (!formData.last_name.trim()) return 'Enter a last name to continue';
    if (!formData.email.trim()) return 'Enter a work email to continue';
    if (!isValidEmail(normalizeEmail(formData.email))) return 'Enter a valid work email to continue';
    if (!formData.dob) return 'Pick a date of birth to continue';
    // Address: line 1 / line 2 are optional; everything else is required.
    const a = formData.address;
    if (!a.country || !a.city || !a.region || !a.postal_code) return 'Complete the address to continue';
    return '';
});
const canSubmitDetails = computed(() => !detailsSubmitReason.value);

/** The manual done screen's License row: the pick, the retry note, or what Auto-Provision did. */
const manualLicenseText = computed(() => {
    if (licenseAssignmentFailed.value) return 'Not assigned — retry from the profile';
    if (selectedLicense.value) return selectedTierName.value;
    return licenseOutcomeText.value || '—';
});

// The done screen's Send invite (D29): a manually added person got no email.
const inviteSend = ref<'idle' | 'sending' | 'sent' | 'failed'>('idle');
async function sendInvite() {
    if (!companyId.value || !createdEmployee.value || inviteSend.value === 'sending') return;
    inviteSend.value = 'sending';
    try {
        await businessService.sendEmployeeInvite(companyId.value, createdEmployee.value.id);
        inviteSend.value = 'sent';
    } catch (error) {
        console.error('Failed to send invite:', error);
        inviteSend.value = 'failed';
    }
}

function getDepartmentName(id: number | null) {
    if (!id) return '';
    const dept = departments.value.find((d) => d.id === id);
    return dept?.name || '';
}

/**
 * Seats are bought in the checkout app, which is a page rather than a dialog —
 * so open it in a NEW TAB. Navigating in place would take this half-filled
 * employee form with it. `awaitingSeats` then offers the re-read, because the
 * purchase lands somewhere this modal cannot observe.
 *
 * `plan` is the tier this button was offered for — the Auto-Provision tier under the
 * invite sentence, the pressed card's tier in the wizard — and it lands the admin on
 * that plan's summary instead of asking them to pick the plan they came from. Empty
 * opens the chooser, as before.
 */
function openPurchaseLicenses(plan?: string) {
    if (!canBuySeats.value || !companyId.value) return;
    // The demo has no auth token, so checkout would bounce the tab to the auth app.
    if (isDemoMode()) {
        showError('Not in the demo', 'Buying licenses opens the real checkout, which the demo cannot reach.');
        return;
    }

    window.open(
        checkoutBusinessUrl(companyId.value, `/business/${companySlug.value ?? ''}/employees`, { plan }),
        '_blank',
        'noopener',
    );
    awaitingSeats.value = true;
}

async function refreshSeats(): Promise<void> {
    // Keep the affordance when the read failed: hiding it on a transient error
    // leaves the form on stale out-of-stock rows with no way back except another
    // checkout tab. The invite form reads the page's seat counts; the wizard reads
    // the priced catalog.
    const ok = flowMode.value === 'invite' ? await fetchSeatCounts() : await fetchLicenses();
    if (ok) awaitingSeats.value = false;
}

// Enter advances the wizard only from a field.
function handleEnter(event: KeyboardEvent) {
    if (!enterFromField(event)) return;
    event.preventDefault();
    if (isLoading.value) return;
    if (flowMode.value === 'choice') {
        if (addMethod.value) startFlow();
    } else if (flowMode.value === 'invite') {
        if (!inviteSuccess.value && canSubmitInvite.value) submitInvite();
        else if (inviteSuccess.value) finishAndClose();
    } else if (flowMode.value === 'manual') {
        if (manualStep.value < 4) manualStepNext();
        else finishAndClose();
    }
}

function resetForm() {
    licenseAssignmentFailed.value = false;
    inviteSend.value = 'idle';
    visitedLicenseStep = false;
    clearFieldErrors();
    formData.email = '';
    formData.first_name = '';
    formData.last_name = '';
    formData.role = null;
    formData.department_id = null;
    formData.dob = '';
    formData.phone = { country_code: '1', phone_number: '' };
    formData.address = { line_1: '', line_2: '', city: '', region: '', postal_code: '', country: 'US' };
    selectedLicense.value = '';
    showDobPicker.value = false;
}

function onDobApplied(value: Date) {
    // Capture as a noon-local Date to dodge timezone day shifts (same trick
    // EditDobModal uses), then store the YYYY-MM-DD string the API expects.
    const local = new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12, 0, 0);
    formData.dob = `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2, '0')}-${String(local.getDate()).padStart(2, '0')}`;
    showDobPicker.value = false;
}

function closeDialog() {
    // Lock every close path while a create request is in flight: dismissing
    // mid-request lets the backend finish after close, but the parent would
    // receive { created: false } and skip its roster refresh, hiding the
    // "canceled" employee until a later reload.
    if (isLoading.value) return;
    dialogRef.value?.close({ created: !!createdEmployee.value });
}

function startFlow() {
    if (addMethod.value === 'invite') {
        flowMode.value = 'invite';
        fetchSeatCounts();
    } else if (addMethod.value === 'manual') {
        flowMode.value = 'manual';
        manualStep.value = 1;
        fetchLicenses();
    }
}

// Choice cards navigate straight into their flow — no separate "Continue" click.
function selectMethod(method: AddMethod) {
    addMethod.value = method;
    startFlow();
}

// Back from the invite form returns to the method choice screen.
function backToChoice() {
    flowMode.value = 'choice';
    addMethod.value = null;
}

/** Returns false when the read failed, so callers can keep their retry on screen. */
async function fetchLicenses(): Promise<boolean> {
    if (!companyId.value) return false;
    try {
        licenses.value = await businessService.getLicenses(companyId.value);
        return true;
    } catch (error) {
        console.error('Failed to load licenses:', error);
        return false;
    }
}

async function submitInvite() {
    if (!companyId.value || !canSubmitInvite.value) return;
    isLoading.value = true;
    clearFieldErrors();
    try {
        // No personal-message field here on purpose: the invite pipeline
        // (request validation, EmployeeCreator, the invite job and E1Mail) does
        // not carry one, so the form must not promise to deliver a custom note.
        const emp = await businessService.createEmployee(companyId.value, {
            email: normalizeEmail(formData.email),
            first_name: formData.first_name,
            last_name: formData.last_name,
            invite: true,
        });
        createdEmployee.value = emp;
        inviteSuccess.value = true;
    } catch (error) {
        handleCreateError(error, 'Failed to send invite');
    } finally {
        isLoading.value = false;
    }
}

// Preselect the company's Auto-Provision tier the first time the license step
// opens (D5); "Skip for now" after that is the admin's call and is not undone.
let visitedLicenseStep = false;

async function manualStepNext() {
    if (manualStep.value === 1) {
        if (!canSubmitDetails.value) return;
        manualStep.value = 2;
    } else if (manualStep.value === 2) {
        if (!visitedLicenseStep) {
            visitedLicenseStep = true;
            const auto = licenseOptions.value.find((o) => o.slug === autoProvisionSlug.value);
            if (auto && !auto.outOfStock) selectedLicense.value = auto.slug;
        }
        manualStep.value = 3;
    } else if (manualStep.value === 3) {
        await submitManual();
    }
}

async function submitManual() {
    if (!companyId.value || !canSubmitDetails.value) return;
    isLoading.value = true;
    clearFieldErrors();
    try {
        // If a prior attempt already created the employee, don't call
        // createEmployee again (it would try to create a second employee with
        // the same email).
        let emp = createdEmployee.value;
        if (!emp) {
            const employeeData: EmployeeCreateInput = {
                email: normalizeEmail(formData.email),
                first_name: formData.first_name,
                last_name: formData.last_name,
                invite: false,
                // The license step's choice, or none, sent explicitly: left out, the
                // API falls back to Auto-Provision and a Skip would still claim a seat (D5).
                license: selectedLicense.value || NO_LICENSE_SLUG,
            };
            // The API accepts role assignments as the `roles` list.
            if (formData.role && canAssignRoles.value) employeeData.roles = [formData.role];
            if (formData.department_id) employeeData.department_id = formData.department_id;

            // Optional sections — only attach when populated enough to satisfy
            // the backend's required-with-validation rules. The service drops
            // any half-filled phone/address objects before posting.
            if (formData.dob) employeeData.dob = formData.dob;

            employeeData.phone_number = optionalPhonePayload(formData.phone);
            employeeData.address = optionalAddressPayload(formData.address);

            emp = await businessService.createEmployee(companyId.value, employeeData);
            createdEmployee.value = emp;
        }

        // The seat was claimed in the same request; the API says whether it took (D8).
        // The employee exists either way, so the wizard advances and the profile is
        // where the seat is retried.
        if (selectedLicense.value && emp.creation?.license_assigned === false) {
            licenseAssignmentFailed.value = true;
            const tier = selectedTierName.value;
            showError('Error', emp.creation.license_reason === 'no_seats'
                ? `Employee added, but no ${tier} licenses are free. Assign one from their profile once licenses are bought.`
                : `Employee added, but the ${tier} license couldn't be assigned. Retry from their profile.`);
        }

        manualStep.value = 4;
    } catch (error) {
        handleCreateError(error, 'Failed to add employee');
    } finally {
        isLoading.value = false;
    }
}

function goToAssignLicense() {
    // Hand the intent back to the employees page instead of opening the drawer
    // here. Dialogs are hosted by the layout, so one opened from this modal
    // outlives it and is not in the page's tracked set — the page could not close
    // it when it unmounts, and it would keep operating on this company's ids over
    // whatever page the reader moved to. Retaining it locally cannot work either:
    // PrimeVue deletes the instance at onDialogAfterHide, so this modal's
    // onBeforeUnmount runs AFTER the drawer opens and would immediately close it.
    // The page owns the tracking and closes what it tracks when it unmounts.
    if (isLoading.value) return;

    dialogRef.value?.close({
        created: !!createdEmployee.value,
        assignLicenseFor: createdEmployee.value?.id,
    });
}

function finishAndClose() {
    closeDialog();
}

function addAnother() {
    flowMode.value = 'choice';
    addMethod.value = null;
    inviteSuccess.value = false;
    manualStep.value = 1;
    createdEmployee.value = null;
    resetForm();
}

async function fetchSupportingData() {
    if (!companyId.value) return;
    // Roles and settings need different permissions — fetch them independently so
    // a 403 on settings (write-employees-but-no-settings admin) doesn't discard the
    // role list and leave the selector empty.
    try {
        const rolesData = await businessService.getRoles(companyId.value);
        // Employee role assignment posts only the bare role name against the
        // business guard, so PB-internal (privacy_bee guard) roles must not be
        // offered — selecting one would hit the ambiguous business-guard
        // assignment path and fail or attach a same-named business role.
        // The `Employee` baseline is applied to everyone and hidden from the
        // roles-permissions page and drawer, so drop it here too rather than let
        // the create flow assign the baseline role.
        roles.value = rolesData
            .filter((r) => r.name !== 'Employee' && r.guard_name !== 'privacy_bee')
            .map((r) => ({ name: r.name }));
    } catch (error) {
        console.error('Failed to load roles:', error);
    }
    try {
        await businessStore.fetchSettings();
        settingsAvailable.value = true;
    } catch (error) {
        settingsAvailable.value = false;
        console.warn('CreateEmployeeModal: settings unavailable for this user.', error);
    }
}

onMounted(async () => {
    await businessStore.fetchBusiness(companySlug.value || '');
    await fetchSupportingData();
});
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/business-form-modal';

.modal-body {
    .choice-prompt {
        @apply text-base font-semibold text-zinc-900 m-0 mb-1;
    }

    .choice-subtitle {
        @apply text-sm text-zinc-500 m-0 mb-6;
    }

    .choice-options {
        @apply flex flex-col gap-3;

        .choice-card {
            @apply flex items-start gap-4 p-4 rounded-xl border-2 border-zinc-200 cursor-pointer transition-colors;

            &:hover {
                @apply border-zinc-300 bg-zinc-50/50;
            }

            &.selected {
                @apply border-blue-600 bg-blue-50/50;
            }

            .choice-icon {
                @apply text-zinc-500 flex-shrink-0 mt-0.5;
            }

            &.selected .choice-icon {
                @apply text-blue-600;
            }

            .choice-content {
                @apply flex flex-col gap-1;

                .choice-title {
                    @apply font-semibold text-zinc-900;
                }

                &.selected .choice-title {
                    @apply text-blue-700;
                }

                .choice-desc {
                    @apply text-sm text-zinc-500;
                }

                &.selected .choice-desc {
                    @apply text-blue-600/80;
                }
            }
        }
    }

    .info-banner {
        @apply p-4 rounded-lg bg-blue-50 text-blue-700 text-sm mb-6;
    }

    .form-row {
        @apply mb-4;

        // The design system's field puts the label 6px above its control.
        label,
        .group-label {
            @apply block text-sm font-medium text-zinc-700 mb-1.5;
        }
    }

    .field-error {
        @apply text-sm text-red-600 mt-1 m-0;
    }

    .field-reason {
        @apply text-sm text-zinc-500 -mt-2 mb-4;
    }

    .inline-link {
        @apply text-sm font-medium text-blue-600 hover:underline ml-1;
    }

    .form-hint {
        @apply block text-xs text-zinc-500 mt-1;
    }

    // Higher specificity than `.form-row label { @apply block; }` above so
    // the flex layout actually wins and Phone/Optional don't collapse into
    // each other.
    label.label-with-tag {
        @apply flex items-center justify-between gap-2;

        .optional-tag {
            @apply text-xs font-normal text-zinc-400;
        }
    }

    .dob-phone-row {
        @apply grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4;

        // Each cell already wraps its own .form-row, which carries its own
        // bottom margin — strip it so the grid gap controls spacing.
        > .form-row {
            @apply mb-0;
        }
    }

    .form-row--dob {
        @apply relative;
    }

    .dob-trigger {
        @apply w-full flex items-center gap-2 px-3 py-2 border border-zinc-300 rounded-md bg-white text-sm text-zinc-800 cursor-pointer transition-colors;

        &:hover {
            @apply border-zinc-400;
        }

        .dob-clear {
            @apply ml-auto p-1 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100;
        }
    }

    // Floats over the form rows below so opening the picker doesn't shift
    // the Phone / Address sections downward.
    .dob-popover {
        @apply absolute left-0 z-30 mt-1 rounded-lg bg-white;
        top: 100%;
        box-shadow: 0 12px 32px -8px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.06);
    }

    .license-hint {
        @apply text-sm text-zinc-500 mt-2;
    }


    .license-instruction {
        @apply text-sm text-zinc-600 mb-4;
    }

    .license-cards {
        @apply space-y-2 mb-4;

        .license-card {
            @apply p-4 rounded-lg border-2 border-zinc-200 cursor-pointer transition-colors;

            &:hover {
                @apply border-zinc-300;
            }

            &.selected {
                @apply border-blue-600 bg-blue-50/50;
            }

            &.disabled {
                @apply opacity-50 cursor-not-allowed bg-zinc-50;

                &:hover {
                    @apply border-zinc-200;
                }
            }

            .license-card-header {
                @apply flex flex-col gap-1;

                .license-name {
                    @apply font-semibold text-zinc-900;
                }

                .license-meta {
                    @apply text-sm text-zinc-500;
                }
            }

            .purchase-more-link {
                @apply text-sm font-medium text-blue-600 hover:underline mt-2 cursor-pointer;
            }
        }
    }

    .skip-link {
        @apply text-sm text-blue-600 hover:underline;
    }
}

.invite-success,
.complete-step {
    @apply text-center;

    .success-icon {
        @apply mb-4 mx-auto block;
    }

    .success-title {
        @apply text-xl font-bold text-zinc-900 m-0 mb-2;
    }

    .success-message {
        @apply text-zinc-600 mb-4;
    }

    .success-summary,
    .complete-summary {
        @apply bg-zinc-50 rounded-lg p-4 mb-4 text-left max-w-sm mx-auto;

        .summary-row {
            @apply flex justify-between py-1.5 text-sm;

            span:first-child {
                @apply text-zinc-500;
            }

            span:last-child {
                @apply font-medium text-zinc-900;
            }
        }
    }

    .info-alert {
        @apply flex items-start gap-3 p-4 bg-blue-50 rounded-lg text-sm text-blue-700 text-left;

        .inline-link {
            @apply text-sm font-medium text-blue-700 underline ml-1;
        }
    }

    .send-invite-row {
        @apply flex flex-col items-center gap-2 mt-4;
    }

    .send-invite-status {
        @apply text-sm text-zinc-600 m-0;

        &--failed {
            @apply text-red-600;
        }
    }
}

.modal-footer .submit-reason {
    @apply flex-1 text-sm text-zinc-500 m-0;
}

.stepper {
    @apply flex items-center gap-0 px-8 pb-6;

    .stepper-item {
        @apply flex items-center gap-2;

        .stepper-circle {
            @apply w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors;
            @apply bg-white border-zinc-200 text-zinc-500;
        }

        &.active .stepper-circle {
            @apply border-blue-600 bg-blue-600 text-white;
        }

        &.completed .stepper-circle {
            @apply border-green-500 bg-green-500 text-white;
        }

        .stepper-label {
            @apply text-sm font-medium text-zinc-500 whitespace-nowrap;
        }

        &.active .stepper-label {
            @apply text-blue-600 font-semibold;
        }

        &.completed .stepper-label {
            @apply text-zinc-700;
        }

        .stepper-line {
            @apply flex-1 h-0 mx-3 min-w-[20px] border-t-2 border-dashed border-zinc-200 self-center;

            &.completed {
                @apply border-green-500 border-solid;
            }
        }
    }
}

</style>
